import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { agencyService } from '../services/api';
import { Building2, Users, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface AgencyInfo {
  id: string;
  name: string;
  slug: string;
  plan: string;
}

interface Member {
  id: string;
  agency_id: string;
  user_id: string;
  role: string;
  email?: string | null;
}

function SkeletonLine({ className }: { className?: string }) {
  return <div className={`bg-muted rounded animate-pulse ${className ?? ''}`} />;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-8">
      {/* Agency header skeleton */}
      <div className="border border-border rounded-xl p-6 space-y-3">
        <SkeletonLine className="h-5 w-48" />
        <SkeletonLine className="h-4 w-24" />
      </div>
      {/* Members skeleton */}
      <div className="border border-border rounded-xl p-6 space-y-4">
        <SkeletonLine className="h-4 w-32" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center justify-between py-2">
            <SkeletonLine className="h-4 w-56" />
            <SkeletonLine className="h-5 w-16 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const isAdmin = role === 'admin';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        isAdmin
          ? 'bg-primary/10 text-primary'
          : 'bg-muted text-muted-foreground'
      }`}
    >
      {isAdmin ? 'Admin' : 'Agente'}
    </span>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
      {plan.toUpperCase()}
    </span>
  );
}

export default function AgencySettingsPage() {
  const [agency, setAgency] = useState<AgencyInfo | null>(null);
  const [myRole, setMyRole] = useState<string>('');
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [notInAgency, setNotInAgency] = useState(false);
  const [error, setError] = useState('');

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteError, setInviteError] = useState('');

  // Copy slug feedback
  const [copied, setCopied] = useState(false);

  // Create agency form state
  const [newAgencyName, setNewAgencyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setNotInAgency(false);

    agencyService
      .getMe()
      .then(async (meData) => {
        if (cancelled) return;
        setAgency(meData.agency);
        setMyRole(meData.role);
        const membersData = await agencyService.getMembers(meData.agency.id);
        if (!cancelled) setMembers(membersData.members);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 403 || status === 404) {
          setNotInAgency(true);
        } else {
          setError('No se pudo cargar la información de la agencia.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agency || !inviteEmail.trim()) return;
    setInviting(true);
    setInviteSuccess('');
    setInviteError('');
    try {
      await agencyService.invite(agency.id, inviteEmail.trim());
      setInviteSuccess(`Invitación enviada a ${inviteEmail.trim()}`);
      setInviteEmail('');
      // Refresh member list
      const membersData = await agencyService.getMembers(agency.id);
      setMembers(membersData.members);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Error al enviar la invitación.';
      setInviteError(msg);
    } finally {
      setInviting(false);
    }
  };

  const handleCreateAgency = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAgencyName.trim()) return;
    setCreating(true);
    setCreateError('');
    try {
      await agencyService.create(newAgencyName.trim());
      // Reload full agency state (role, members) after creation
      const meData = await agencyService.getMe();
      setAgency(meData.agency);
      setMyRole(meData.role);
      const membersData = await agencyService.getMembers(meData.agency.id);
      setMembers(membersData.members);
      setNotInAgency(false);
      setNewAgencyName('');
    } catch {
      setCreateError('Error al crear la agencia. Intenta de nuevo.');
    } finally {
      setCreating(false);
    }
  };

  // Aviso que llega por state de navegación (p. ej. invitación no aplicada — gate R2)
  const location = useLocation();
  const navigate = useNavigate();
  const [notice] = useState<string>(() => (location.state as { notice?: string } | null)?.notice ?? '');
  useEffect(() => {
    // Gate R3: history.state sobrevive a F5 y a "atrás"; se consume tras mostrarlo
    if ((location.state as { notice?: string } | null)?.notice) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [copiedLink, setCopiedLink] = useState<'idle' | 'ok' | 'fail'>('idle');
  const publicSearchUrl = agency
    ? `${window.location.origin}/buscar?agency=${agency.id}`
    : '';
  const handleCopyPublicLink = async () => {
    if (!publicSearchUrl) return;
    // Gate R1: sin clipboard (http por IP de LAN) o si writeText falla, no
    // decir "Copiado": el link queda visible para seleccionarlo a mano.
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicSearchUrl);
        ok = true;
      }
    } catch {
      ok = false;
    }
    setCopiedLink(ok ? 'ok' : 'fail');
    window.setTimeout(() => setCopiedLink('idle'), 2500);
  };

  const handleCopySlug = async () => {
    if (!agency) return;
    // Gate R2: sin clipboard (http en LAN) `navigator.clipboard` es undefined y
    // esto lanzaba TypeError; se copia solo si existe y resuelve.
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(agency.slug);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      /* sin portapapeles: el slug queda visible para seleccionarlo */
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {notice && (
          <div role="status" className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {notice}
          </div>
        )}
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Agencia</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Administra tu agencia y el equipo de trabajo.
          </p>
        </div>

        {/* Loading */}
        {loading && <LoadingSkeleton />}

        {/* Error */}
        {!loading && error && (
          <div className="border border-destructive/40 bg-destructive/5 text-destructive rounded-xl px-5 py-4 text-sm">
            {error}
          </div>
        )}

        {/* Not in an agency */}
        {!loading && notInAgency && (
          <div className="border border-border rounded-xl p-8 space-y-4 max-w-md mx-auto">
            <div className="text-center space-y-3">
              <Building2 className="w-10 h-10 text-muted-foreground mx-auto" />
              <h2 className="font-semibold text-base">Aún no perteneces a una agencia</h2>
              <p className="text-sm text-muted-foreground">
                Crea tu agencia para comenzar a gestionar propiedades y agentes.
              </p>
            </div>
            <form onSubmit={handleCreateAgency} className="space-y-3">
              <Input
                type="text"
                placeholder="Nombre de la agencia"
                value={newAgencyName}
                onChange={(e) => setNewAgencyName(e.target.value)}
                required
                minLength={2}
                disabled={creating}
              />
              <p className="text-xs text-muted-foreground">
                El identificador (slug) se genera automáticamente a partir del nombre.
              </p>
              {createError && <p className="text-sm text-destructive">{createError}</p>}
              <Button type="submit" className="w-full" disabled={creating || !newAgencyName.trim()}>
                {creating ? 'Creando agencia…' : 'Crear agencia'}
              </Button>
            </form>
          </div>
        )}

        {/* Main content */}
        {!loading && agency && (
          <div className="space-y-6">
            {/* Agency info card */}
            <section className="border border-border rounded-xl p-6">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-base leading-tight">{agency.name}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground font-mono">{agency.slug}</span>
                      <button
                        onClick={handleCopySlug}
                        aria-label="Copiar slug"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        title="Copiar slug"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      {copied && (
                        <span className="text-xs text-green-600">Copiado</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <PlanBadge plan={agency.plan} />
                  <RoleBadge role={myRole} />
                </div>
              </div>
              {/* QA 2026-10-17 (F-004): el buscador público exige ?agency=<UUID>;
                  sin este bloque ningún admin podía obtener su link. */}
              <div className="mt-5 pt-5 border-t border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-widest font-medium mb-2">
                  Tu buscador público
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-xs font-mono bg-muted px-2 py-1 rounded break-all">{publicSearchUrl}</code>
                  <button
                    onClick={handleCopyPublicLink}
                    aria-label="Copiar link del buscador"
                    title="Copiar link del buscador"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  {copiedLink === 'ok' && <span className="text-xs text-green-600">Copiado</span>}
                  {copiedLink === 'fail' && (
                    <span className="text-xs text-destructive">No se pudo copiar; selecciónalo a mano.</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Compártelo en tus anuncios: los interesados buscan sin cuenta y sus datos llegan a Leads.
                </p>
              </div>
            </section>

            {/* Members card */}
            <section className="border border-border rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-medium text-sm">
                  Miembros{' '}
                  <span className="text-muted-foreground font-normal">({members.length})</span>
                </h3>
              </div>

              {members.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No hay miembros aún.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {members.map((m) => (
                    <li key={m.id} className="flex items-center justify-between py-3">
                      <span className="text-sm font-mono text-muted-foreground">
                        {m.email ?? `${m.user_id.slice(0, 8)}…${m.user_id.slice(-4)}`}
                      </span>
                      <RoleBadge role={m.role} />
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Invite form card */}
            <section className="border border-border rounded-xl p-6">
              <h3 className="font-medium text-sm mb-4">Invitar agente</h3>
              <form onSubmit={handleInvite} className="flex gap-2 flex-wrap">
                <Input
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  className="flex-1 min-w-[220px]"
                  disabled={inviting}
                />
                <Button type="submit" disabled={inviting || !inviteEmail.trim()}>
                  {inviting ? 'Enviando…' : 'Invitar'}
                </Button>
              </form>

              {inviteSuccess && (
                <p className="mt-3 text-sm text-green-600">{inviteSuccess}</p>
              )}
              {inviteError && (
                <p className="mt-3 text-sm text-destructive">{inviteError}</p>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
